import { Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import { isPublicAppPath, PATHS } from './utils/routes';
import { StatusBarProvider } from './components/ui/StatusBarManager.jsx';
import LoadingFallback from './components/ui/LoadingFallback.jsx';
import PwaInstallGlobalPrompt from './components/ui/PwaInstallGlobalPrompt.jsx';

const AppRoutes = lazy(() => import('./routes/AppRoutes.jsx'));
const AppRuntimeShell = lazy(() => import('./components/app/AppRuntimeShell.jsx'));
const Toaster = lazy(() => import('react-hot-toast').then((mod) => ({ default: mod.Toaster })));

function RouteBootstrapFallback() {
  return <LoadingFallback />;
}

export default function App() {
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading: authLoading,
    user: auth0User,
    getAccessTokenSilently,
  } = useAuth0();

  const isPublicRoute = isPublicAppPath(location.pathname);
  const isPublicAnonymous = isPublicRoute && !authLoading && !isAuthenticated;
  const shouldHoldPublicBoot = isPublicRoute && authLoading && location.pathname === PATHS.root;

  const routesNode = (
    <Suspense fallback={<RouteBootstrapFallback />}>
      <AppRoutes />
    </Suspense>
  );

  if (shouldHoldPublicBoot) {
    return (
      <StatusBarProvider>
        <RouteBootstrapFallback />
        <PwaInstallGlobalPrompt />
        <Suspense fallback={null}>
          <Toaster position="top-center" reverseOrder={false} />
        </Suspense>
      </StatusBarProvider>
    );
  }

  return (
    <StatusBarProvider>
      {isPublicAnonymous ? (
        routesNode
      ) : (
        <Suspense fallback={routesNode}>
          <AppRuntimeShell
            isPublicRoute={isPublicRoute}
            pathname={location.pathname}
            isAuthenticated={isAuthenticated}
            authLoading={authLoading}
            auth0User={auth0User}
            getAccessTokenSilently={getAccessTokenSilently}
          >
            {routesNode}
          </AppRuntimeShell>
        </Suspense>
      )}
      <PwaInstallGlobalPrompt />
      <Suspense fallback={null}>
        <Toaster position="top-center" reverseOrder={false} />
      </Suspense>
    </StatusBarProvider>
  );
}
